import { useStaticQuery, graphql } from "gatsby";
import React from "react";
import {
  PageBuilderBrochureLeadFormStaticThankYouPagesQuery,
  PageType
} from "types/graphql-types";
import { isPresent } from "./checks";
import { Geolocation } from "./localization/geolocation/geolocation";
import { Language, LanguageCode } from "./localization/languages/types";

export type BrochureLanguageCode = LanguageCode | "it" | "ru";

type BrochureProto = {
  language?: string;
  isActive?: boolean;
  countries?: (string | undefined)[];
};

export const filterBrochuresByLocation = <T extends BrochureProto>(
  brochures: (T | undefined)[],
  geolocation: Geolocation | undefined
): T[] => {
  if (!isPresent(brochures)) return [];

  const filterBy = filterBrochure(geolocation);
  return brochures.filter(isPresent).filter(filterBy) || [];
};

const filterBrochuresByLocationAndLanguage = <T extends BrochureProto>(
  brochures: (T | undefined)[],
  geolocation: Geolocation | undefined,
  language: Language
): T[] => {
  if (!isPresent(brochures) || !language) return [];
  let filteredByLanguage = brochuresForLanguage(language.code, brochures);
  if (filteredByLanguage.length == 0)
    filteredByLanguage = brochuresForLanguage("en", brochures);

  return filterBrochuresByLocation(filteredByLanguage, geolocation);
};

// eslint-disable-next-line import/no-unused-modules
export const filterBrochure =
  (geolocation: Geolocation | undefined) =>
  <T extends BrochureProto>(brochure: T): brochure is T => {
    if (!brochure.isActive) return false;
    const currentCountry = geolocation?.countryCode;

    if (!geolocation) return false;
    return isPresent(currentCountry)
      ? brochure.countries?.filter(isPresent).includes(currentCountry) || false
      : false;
  };

export const brochuresForLanguage = <T extends { language?: string }>(
  language: BrochureLanguageCode,
  brochures: (T | undefined)[]
): T[] => {
  return brochures.filter((b) => b?.language == language).filter(isPresent);
};

export const useTargetBrochure = <T extends BrochureProto>(
  brochures: T[] | undefined,
  geolocation: Geolocation,
  language: Language
): T | undefined => {
  const filteredBrochures = React.useMemo(() => {
    if (!isPresent(brochures) || !isPresent(geolocation)) return [];
    return filterBrochuresByLocationAndLanguage(
      brochures,
      geolocation,
      language
    );
  }, [brochures, geolocation, language]);
  return filteredBrochures[0];
};

export const useTargetBrochures = <T extends BrochureProto>(
  brochures: T[] | undefined,
  geolocation: Geolocation,
  language: Language
): T[] | undefined => {
  const filteredBrochures = React.useMemo(() => {
    if (!isPresent(brochures) || !isPresent(geolocation)) return [];
    return filterBrochuresByLocationAndLanguage(
      brochures,
      geolocation,
      language
    );
  }, [brochures, geolocation, language]);
  return filteredBrochures;
};

export const useBrochureThankYouPageURL = (
  language: Language,
  pageType: PageType
): string | undefined => {
  const thankYouPages =
    useStaticQuery<PageBuilderBrochureLeadFormStaticThankYouPagesQuery>(graphql`
      query PageBuilderBrochureLeadFormStaticThankYouPages {
        allSitePage(
          filter: {
            context: {
              sanityId: { regex: "/request-brochure-thankyou-page$/" }
            }
          }
        ) {
          nodes {
            context {
              languageCode
              pageType
            }
            path
          }
        }
      }
    `);

  const thankYouPage = thankYouPages.allSitePage.nodes.find(
    (n) =>
      n.context?.languageCode == language.code && n.context.pageType == pageType
  );

  return thankYouPage?.path;
};
